/**
 * A zone's colour: automatic, or one of the eight the zone maps are drawn in.
 *
 * Automatic is the default and the right answer for most zones — `huesFor` keeps a zone
 * apart from its kin and its neighbours. A swatch is for the zone that came out too like the
 * one beside it, or the district the office already calls "the green one" (#373). The choice
 * is stored on the zone and survives every zone added after it.
 *
 * The swatches are tokens, `bg-zone-1` … `bg-zone-8`, so they are the colours the maps draw in
 * under either theme. Written out in full, because Tailwind finds classes by reading the
 * source and never sees one built from a template.
 */

import { useTranslation } from 'react-i18next';

import { cx } from '../../ui';

const SWATCHES = [
  'bg-zone-1',
  'bg-zone-2',
  'bg-zone-3',
  'bg-zone-4',
  'bg-zone-5',
  'bg-zone-6',
  'bg-zone-7',
  'bg-zone-8',
] as const;

export function HuePicker({
  id,
  value,
  automatic,
  onChange,
}: {
  /** Given by `Field`, so its label names this group. */
  id?: string;
  /** The stored colour, or null for automatic. */
  value: number | null;
  /** The colour automatic currently gives this zone, when it is known. */
  automatic: number | null;
  onChange: (hue: number | null) => void;
}) {
  const { t } = useTranslation();

  return (
    <div id={id} role="radiogroup" className="flex flex-wrap items-center gap-2">
      <button
        type="button"
        role="radio"
        aria-checked={value === null}
        onClick={() => onChange(null)}
        className={cx(
          'inline-flex items-center gap-2 h-8 px-2.5 rounded-control border text-secondary',
          value === null ? 'border-brand text-ink font-medium' : 'border-rule text-ink-2',
        )}
      >
        {automatic !== null && (
          <span aria-hidden className={cx('w-3.5 h-3.5 rounded-full', SWATCHES[automatic])} />
        )}
        {t('zones.hueAutomatic')}
      </button>
      {SWATCHES.map((swatch, hue) => (
        <button
          key={swatch}
          type="button"
          role="radio"
          aria-checked={value === hue}
          aria-label={t('zones.hueNumber', { number: hue + 1 })}
          title={t('zones.hueNumber', { number: hue + 1 })}
          onClick={() => onChange(hue)}
          className={cx(
            'w-8 h-8 rounded-control border-2 p-1',
            value === hue ? 'border-ink' : 'border-transparent',
          )}
        >
          <span className={cx('block w-full h-full rounded-[4px]', swatch)} />
        </button>
      ))}
    </div>
  );
}
